import { useEffect, useRef, useState } from "react";
import { usePrefersReducedMotion } from "./usePrefersReducedMotion";
import { useIsFirstModule } from "@/context/IsFirstModuleContext";

export type AnimationType =
  | "fade-in"
  | "fade-in-up"
  | "fade-in-down"
  | "fade-in-up-small"
  | "fade-in-up-large"
  | "fade-in-down-large"
  | "slide-left"
  | "slide-right"
  | "slide-left-large"
  | "slide-right-large"
  | "slide-up-no-opacity"
  | "scale-up"
  | "rotate-in"
  | "clip-horizontal"
  | "height-expand"
  | "clip-circle-reveal";

interface UseScrollAnimationOptions {
  animation?: AnimationType;
  threshold?: number;
  triggerOnce?: boolean;
  delay?: number;
  duration?: number;
  disabled?: boolean;
  disableThreshold?: boolean;
  viewport?: {
    amount?: number;
    margin?: string;
  };
}

interface UseScrollAnimationReturn {
  ref: React.RefObject<HTMLDivElement>;
  isInView: boolean;
  className: string;
}

/**
 * Hook to handle scroll-based animations using IntersectionObserver
 *
 * @param options - Configuration options for the animation
 * @returns Object containing ref, isInView state, and generated className
 */
export const useScrollAnimation = (
  options: UseScrollAnimationOptions = {},
): UseScrollAnimationReturn => {
  const {
    animation = "fade-in-up",
    threshold = 0.3,
    triggerOnce = true,
    delay,
    duration,
    disabled = false,
    disableThreshold = false,
    viewport,
  } = options;

  const isFirstModule = useIsFirstModule();
  const effectiveDisableThreshold = disableThreshold || isFirstModule;

  const ref = useRef<HTMLDivElement>(null);
  const [isInView, setIsInView] = useState(false);
  const [hasTriggered, setHasTriggered] = useState(false);
  const prefersReducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    // Skip if disabled or user prefers reduced motion
    if (disabled || prefersReducedMotion) {
      setIsInView(true);
      return;
    }

    const element = ref.current;
    if (!element) return;

    // Calculate threshold based on element height
    let effectiveThreshold = viewport?.amount ?? threshold;

    if (effectiveDisableThreshold) {
      effectiveThreshold = 0;
    } else {
      // Auto-adjust threshold for large elements (>120vh)
      const viewportHeight = window.innerHeight;
      const elementHeight = element.offsetHeight;

      if (elementHeight > viewportHeight * 1.2) {
        effectiveThreshold = 0;
      }
    }

    // Create IntersectionObserver
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // Use requestAnimationFrame for smooth initial check
            requestAnimationFrame(() => {
              setIsInView(true);
              setHasTriggered(true);
            });

            // Disconnect if triggerOnce is true
            if (triggerOnce) {
              observer.disconnect();
            }
          } else if (!triggerOnce && hasTriggered) {
            // Reset animation if triggerOnce is false
            setIsInView(false);
          }
        });
      },
      {
        threshold: effectiveThreshold,
        rootMargin: viewport?.margin ?? "0px",
      },
    );

    observer.observe(element);

    // Cleanup
    return () => {
      observer.disconnect();
    };
  }, [
    disabled,
    prefersReducedMotion,
    threshold,
    triggerOnce,
    effectiveDisableThreshold,
    viewport,
    hasTriggered,
  ]);

  // Generate className based on options
  const getClassName = (): string => {
    const classes: string[] = [];

    // Add animation class
    classes.push(`animate-${animation}`);

    // Add in-view class if visible
    if (isInView || disabled || prefersReducedMotion) {
      classes.push("in-view");
    }

    // Add delay class
    if (delay && !prefersReducedMotion) {
      classes.push(`anim-delay-${delay}`);
    }

    // Add duration class
    if (duration && !prefersReducedMotion) {
      const durationMap: Record<number, string> = {
        400: "anim-faster",
        600: "anim-fast",
        1500: "anim-slow",
        2000: "anim-slower",
      };

      if (durationMap[duration]) {
        classes.push(durationMap[duration]);
      }
    }

    return classes.join(" ");
  };

  return {
    ref,
    isInView: isInView || disabled || prefersReducedMotion,
    className: getClassName(),
  };
};
